import {html, HTMLTemplateResult} from "lit";
import {property} from "lit/decorators/property.js";
import {when} from "lit/directives/when.js";
import {AbstractLitElement} from "../../abstract-lit.element";
import type {LinkTileDraft} from "../../../helpers/link-tile.helper";
import type {LinkTarget} from "../../../domain/entities/link-targets/link-target";
import {LINK_TILE_ICONS} from "../../../domain/entities/link-tile-icon.const";
import {TileEventName} from "./tile-event-name.enum";

/** Everything a Linkview tile carries EXCEPT its link target — text (an
 *  `RteValue`, see its own doc comment, since 2026-09-07; its own
 *  colour/highlight formatting baked into the value itself — there is no
 *  more separate text-colour field), tile colour, image, icon. Kept separate
 *  from `target` on purpose (a design
 *  decision made during planning): a concrete tile component's own `target`
 *  property carries the SPECIFIC `LinkTarget` subtype it renders, a second
 *  copy on `chrome` would be a second, potentially diverging source of truth
 *  for the same value. */
export type LinkTileChrome = Omit<LinkTileDraft, 'target'>;

/**
 * Shared base for the "one Lit component per Linkview link-target kind"
 * family (Anke, 2026-09-04: "für jedes tile eine eigene Component... die auf
 * einer gemeinsamen AbstractTitleComponent [sic, gemeint: AbstractTileComponent]
 * basiert") — `dashboard-creator`
 * mounts exactly ONE of these per Gridstack tile (see `_mountTile()`),
 * replacing the previous imperative `document.createElement` DOM-building.
 *
 * Generic over `TTarget` (a design decision made during planning) so `target` can
 * live HERE, on the base class, typed per concrete subclass
 * (`AbstractTileComponent<ExternalLinkTarget>` etc.) — without this,
 * `tile-registry.ts`'s `new () => AbstractTileComponent<...>` constructor
 * type wouldn't compile (`typeof AbstractTileComponent` alone is abstract,
 * TS2511), and `_mountTile()` couldn't assign `.target` without an unsound
 * cast.
 *
 * Light DOM (ADR-007, via `AbstractLitElement`) — sits directly inside
 * Gridstack's `.grid-stack-item-content`, which this component's
 * `connectedCallback()` marks with the shared `.dashboard-tile` class so
 * `linkview-view.css` can target ALL tile kinds with one selector (no per-tag-name
 * CSS rule to remember when a new kind is added, see that rule's own
 * comment).
 */
export abstract class AbstractTileComponent<TTarget extends LinkTarget | null = LinkTarget | null> extends AbstractLitElement {

    @property({attribute: false})
    public chrome!: LinkTileChrome;

    @property({attribute: false})
    public target!: TTarget;

    /** Locked (default) = read-only: neither edit nor delete button is even
     *  built (see `render()`) — deliberately WITHOUT `reflect: true`, so no
     *  `locked` HTML attribute exists that a future CSS rule could be
     *  tempted to gate visibility on instead of the buttons genuinely never
     *  existing. */
    @property({type: Boolean})
    public locked = false;

    public override connectedCallback(): void {
        super.connectedCallback();
        this.classList.add('dashboard-tile');
    }

    protected render(): HTMLTemplateResult {
        return html`
            ${this._renderBody()}
            ${when(!this.locked, () => html`
                <button type="button"
                        class="edit dashboard-tile__edit"
                        data-label=""
                        aria-label="Kachel bearbeiten"
                        @click=${this._onClickEdit}
                ></button>
                <button type="button"
                        class="delete dashboard-tile__delete"
                        data-label=""
                        aria-label="Kachel löschen"
                        @click=${this._onClickDelete}
                ></button>
            `)}
        `;
    }

    /** The clickable/non-clickable core of the tile — overridden per
     *  concrete kind. Default (used by `LinkTilePlainComponent`, i.e. no
     *  `target` set) renders a truly non-interactive wrapper around icon+text
     *  — deliberately WITHOUT `role="button"`/`tabindex` (review Code-Lauf 01,
     *  MINOR: an earlier version had both here despite no click/keydown
     *  handler ever being attached, announcing a button to screen readers and
     *  a browser's Tab order that did nothing when activated).
     *  `LinkTileKnowledgeObjectComponent._renderBody()` adds its OWN
     *  `role="button"` + handlers, since it IS interactive. */
    protected _renderBody(): HTMLTemplateResult {
        return html`
            <div class="dashboard-tile__link">
                ${this._renderIconAndText()}
            </div>
        `;
    }

    /**
     * Icon (if any) above the text. `text` is a rich-text value (`RteValue`)
     * since 2026-09-07 (a Quill Delta until the RTE unification, 2026-09-14;
     * a `RichTextDocument` for good once next saved) — rendered via the same
     * read-only `ha-quill-viewer` every other rich-text field in this app
     * uses (`ha-rte-viewer`, the issue description), not a plain-string
     * interpolation; per-run colour lives IN the value itself, so there is
     * no more separate `styleMap()` colour to apply here.
     */
    protected _renderIconAndText(): HTMLTemplateResult {
        const iconDef = this.chrome.icon ? LINK_TILE_ICONS.find((i) => i.id === this.chrome.icon) : undefined;
        return html`
            ${when(iconDef, () => html`
                <i class="dashboard-tile__icon">${String.fromCodePoint(parseInt(iconDef!.codepoint, 16))}</i>
            `)}
            <ha-quill-viewer class="dashboard-tile__text" .value=${this.chrome.text}></ha-quill-viewer>
        `;
    }

    private _onClickEdit(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.fire(undefined, TileEventName.EDIT);
    }

    private _onClickDelete(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.fire(undefined, TileEventName.DELETE);
    }

}
